import { PaymentData, SitePlanInfo } from '@hotjar/webapp/billing/plan';
import { prettifyPriceWithCurrencySymbol } from '@hotjar/webapp/billing/cost';
import { subscriptionAPI, SubscriptionChangePriceSummary } from '@hotjar/webapp/billing/subscription';
import { pollGetOrderStatus } from '@hotjar/webapp/domains/billing/orders';
import { getCustomer } from '@hotjar/webapp/domains/billing/customers/api';
import { mapCustomerEntityToLegacyPriceSummaryLineItem } from '@hotjar/webapp/domains/billing/customers/utils';
import { Decimal } from 'decimal.js';
import { MODAL_LOADED_REFERRER } from '@hotjar/checkout';
import { SUBSCRIPTION_COST_SUMMARY_BUSINESS } from '@hotjar/webapp/domains/revenue/constants/upsellReferrers';
import { getParameter, clearParameters, getUrlPathAndSearch } from '@hotjar/insights-common/lib/url';
import { SUPPORT_URLS } from '@hotjar/webapp/constants/support';
import { isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';
import { hasBundleDiscount } from '@hotjar/webapp/domains/billing/discounts';

angular
    .module('hjSettingsModule')
    .controller(
        'BillingV2Ctrl',
        function (
            $filter,
            $rootScope,
            $scope,
            $window,
            $q,
            $location,
            Account,
            Help,
            Payments,
            Notify,
            Plan,
            restDefaultHandlers,
            Pricing,
            Subscription,
            UserData,
            Loader,
            Track
        ) {
            /*************************
             * PRIVATE VARIABLES
             *************************/

            /**
             * Class which holds the presentational data for the 'update summary'
             * green bar that appears when plans are being updated.
             * @deprecated: Please use CostSummary from packages/legacy/src/webapp/billing/subscription.ts
             */
            class CostSummary {
                constructor(total, subtotal, yearlyDiscount, currencySymbol) {
                    const prettifyPrice = prettifyPriceWithCurrencySymbol(currencySymbol);
                    this.total = total ? prettifyPrice(total) : '';
                    this.subtotal = subtotal ? prettifyPrice(subtotal) : '';
                    this.yearlyDiscount = yearlyDiscount;
                    this.discounted = total && !total.eq(subtotal);
                }

                /**
                 * Factory for constructing a CostSummary from a PriceSummary which is
                 * the result of previewing the cost of a set of plans for the Account.
                 */
                static fromPriceSummary(priceSummary, currencySymbol) {
                    const { total, subtotal } = priceSummary;
                    const yearlyDiscount = priceSummary.deductions.find(({ id }) => id === 'ANNUAL_DEDUCTION');
                    return new CostSummary(total, subtotal, yearlyDiscount, currencySymbol);
                }
            }

            /**
             * Class which encapsulates the presentational data which describes a
             * Site's cost. This will get updated if the user selects a new plan.
             * @deprecated: Please use SitePriceInfo from packages/legacy/src/webapp/billing/subscription.ts
             */
            class SitePriceInfo {
                constructor(
                    total,
                    subtotal,
                    discountPercentage,
                    planId,
                    legacyDiscount = new Decimal(0),
                    currencySymbol = ''
                ) {
                    const hasLegacyDiscount = !legacyDiscount.eq(0);
                    const prettifyPrice = prettifyPriceWithCurrencySymbol(currencySymbol);

                    this.total = total ? prettifyPrice(total) : '';
                    this.subtotal = subtotal ? prettifyPrice(subtotal) : '';
                    this.discountPercentage = discountPercentage;
                    this.showSubtotal = total.gt(0) && !total.eq(subtotal) && !hasLegacyDiscount;
                    this.showPercentage = total.gt(0) && discountPercentage > 0;
                    this.legacyDiscount = hasLegacyDiscount ? prettifyPrice(legacyDiscount) : undefined;
                    this.bundleDiscount = hasBundleDiscount(planId);
                }

                /**
                 * Factory for constructing a SitePriceInfo from an optional
                 * LineItem
                 */
                static fromLineItem(maybeLineItem, currencySymbol) {
                    return new SitePriceInfo(
                        maybeLineItem?.total ?? new Decimal(0),
                        maybeLineItem?.subtotal ?? new Decimal(0),
                        maybeLineItem?.discountPercentage ?? 0,
                        maybeLineItem?.sitePlanInfo.plan.identifier,
                        maybeLineItem?.deductions.find(({ id }) => id === 'LEGACY')?.amount ?? new Decimal(0),
                        currencySymbol
                    );
                }
            }

            function shouldPreviewPrices(account) {
                return account.billing_cycle !== 'lifetime';
            }

            /**
             * Class to encapsulate the summary of changes being made to plans and
             * a summary of the costs associated with these changes.
             */
            class Changes {
                constructor(plans = [], costSummary = new CostSummary()) {
                    this.plans = plans;
                    this.cost = costSummary;
                }
            }

            let account = Account.getOwned();

            const pricingTable = Pricing.byAccount(account);

            let originalSites = [];
            let sites = [];

            /*************************
             * SCOPE VARIABLES
             *************************/
            $scope.model = {
                account: account,
                loaded: false,
                organizations: [],
                siteCount: 0,
                submitting: false,
                subscriptionNote: subscriptionNoteType(),
                zuoraEnabled: isZuoraEnabled(account),
                transactionPending: false,
                transactionFailed: false
            };

            $scope.changes = new Changes();

            $scope.subscriptionInfo = null;

            $scope.canAccessBillingV2 = account.billing_version === 2;

            $scope.updatedSitePriceInfo = null;

            // Databag for the new  `bill_new_update_plans_modal` experiment
            $scope.planChangeConfirmation = {};

            /*************************
             * INITIALIZATION
             *************************/
            if ($scope.canAccessBillingV2) {
                init();
            }

            $rootScope.$on('paymentMethodChanged', init);
            $rootScope.$on('sitePlansChanged', init);

            /*************************
             * PRIVATE FUNCTIONS
             *************************/
            function init() {
                return $q
                    .all({
                        organizations: Account.getOrganizations(account, 'name'),
                        accountSites: Account.getSites(account, 'name'),
                        subscriptionInfo: Subscription.getInfo($scope.model.account),
                        priceSummary: initPriceInfo(),
                        customer: $scope.model.zuoraEnabled
                            ? getCustomer(account.id).catch(() => null)
                            : $q.resolve(null)
                    })
                    .then(({ organizations, accountSites, subscriptionInfo, priceSummary, customer }) => {
                        // This is a little unclear, but actions like deleting a
                        // payment method triggers a user-data refresh under the
                        // hood. This init function requires the account to be as
                        // up to date as possible as its relying on stateful
                        // information such as 'has_payment_details'.
                        //
                        // Ideally we re-fetch everything we need here, such as
                        // the account's payment method, and we don't have to rely
                        // on the side-effect of the user-data getting refreshed
                        // for this to work, but lets maintain this for now.
                        account = Account.getOwned();

                        // Normalize and fill out site object to have full plan
                        // info. We copy the sites as we mutate them as to leave
                        // accountSites untouched in case we need to the original
                        // data at some point.
                        //
                        // TODO(rob.lester): Fill out the various attributes which
                        // are currently determined at digest time, such as "is this
                        // site in a fair downgrade period", to speed up the
                        // digests.
                        sites = accountSites.map((site) => {
                            // in case of yearly we're defaulting prices to those from the pricing table
                            //  for lifetime accounts all sites are free (priceSummary.future.lineItems will be empty)
                            let priceInfo;
                            const currencySymbol = window.settings.currency_symbols[account.currency];

                            if (shouldPreviewPrices(account)) {
                                priceInfo = SitePriceInfo.fromLineItem(
                                    $scope.model.zuoraEnabled
                                        ? mapCustomerEntityToLegacyPriceSummaryLineItem({
                                              customer,
                                              entity: {
                                                  id: site.id,
                                                  name: site.name,
                                                  organizationId: site.organization_id,
                                                  plan: Plan.byId(site.plan_id)
                                              }
                                          })
                                        : priceSummary.future.lineItems.find(
                                              (item) => item.sitePlanInfo.siteReference.siteId === site.id
                                          ),
                                    currencySymbol
                                );
                            } else {
                                priceInfo = new SitePriceInfo(
                                    pricingTable[site.effective_plan_id],
                                    pricingTable[site.effective_plan_id],
                                    0,
                                    site.effective_plan_id,
                                    new Decimal(0),
                                    currencySymbol
                                );
                            }

                            return _.assign({}, site, {
                                id: site.id,
                                organization: _.find(organizations, {
                                    id: site.organization_id
                                }),
                                plan: Plan.byId(site.plan_id),
                                effectivePlan: Plan.byId(site.effective_plan_id),
                                interimPlan: Plan.byId(site.interim_plan_id),
                                priceInfo,
                                changed: false
                            });
                        });

                        // We need a way to compare changes to the site list with
                        // changes the user makes, and easily reset them, so keep
                        // a copy of the original sites.
                        originalSites = angular.copy(sites);

                        $scope.model.siteCount = sites.length;

                        // Since we're going to loop over the organizations for
                        // rendering, create our tree structure of
                        // organizations => sites
                        $scope.model.organizations = organizations.map((organization) => {
                            // From the site models, get the sites which belong to
                            // this organization for when we iterate over it in the
                            // template
                            const organizationSites = _.filter(sites, { organization_id: organization.id });

                            return _.assign(organization, { sites: organizationSites });
                        });

                        // Reset the changes performed by the user
                        $scope.changes = new Changes();

                        // TODO(rob.lester): Change this to Payments.getPaymentMethod
                        // and use the priceSummary instead of the has_paid_plan
                        const needsPaymentDetails = account.has_paid_plan && !account.has_payment_details;
                        const paymentDue = needsPaymentDetails && Account.daysUntilNextBillingDate(account);

                        $scope.subscriptionInfo = subscriptionInfo;
                        $scope.subscriptionInfo.alerts = {
                            recurringPaymentFailed: subscriptionInfo.warnings.pastDue,
                            trialEndingSoon: paymentDue && paymentDue < 60
                        };

                        $scope.subscriptionPriceSummary = priceSummary;

                        // reset sitePriceInfo so that the site prices are returned to
                        // initial value.
                        $scope.updatedSitePriceInfo = null;

                        // start polling transactions in progress
                        checkTransactionStatus();
                    })
                    .finally(() => {
                        $scope.model.loaded = true;
                        checkForPostLoadActions();
                    });
            }

            /**
             * check to see if there is a transaction in progress and if there is,
             * start polling until it is successful/fails
             */
            function checkTransactionStatus() {
                // this is only zuora related functionality so if you are not zuora move along
                if (!$scope.model.zuoraEnabled) {
                    return;
                }

                const POLLING_INTERVAL = 2000;
                pollGetOrderStatus($scope.model.account.id, POLLING_INTERVAL, (status) => {
                    // if we were processing before and the status changed, reload the page to reflect those changes
                    if ($scope.model.transactionPending && status !== 'processing') {
                        $window.location.reload();
                    }
                    $scope.model.transactionPending = status === 'processing';
                    $scope.model.transactionFailed = status === 'failed';
                });
            }

            /**
             * Check if we need to execute any actions based on querystrings.
             * These actions are typically performed through links in emails,
             * such as "update your payment details" links.
             */
            function checkForPostLoadActions() {
                var action = getParameter($location.absUrl(), 'action'),
                    referrer = getParameter($location.absUrl(), 'action_referrer');

                switch (action) {
                    case 'switch_to_yearly_billing_cycle':
                        // Only proceed if we're on a monthly billing cycle.
                        if ($scope.model.account.billing_cycle === 'monthly') {
                            $scope.updatePaymentMethod(referrer, 'yearly_recurring', false);
                        } else {
                            Notify.info('Your account is already on yearly billing or is not eligible for it.');
                        }

                        break;
                    case 'update_payment_method':
                        $scope.updatePaymentMethod(referrer);
                        break;
                }

                // Clear out the parameters so the action is only executed once.
                const nextUrl = getUrlPathAndSearch(clearParameters($location.absUrl(), ['action', 'action_referrer']));

                $location.url(nextUrl);
            }

            /**
             * Function to wrap previewing the subscription cost for Angular
             */
            function initPriceInfo(costSpecs = []) {
                // in case of yearly and lifetime we're not calling Subscription preview endpoint
                //  since those options are not supported and we're defaulting to empty Price Summary
                if (shouldPreviewPrices(account)) {
                    return $q((resolve) => {
                        subscriptionAPI.preview(account, costSpecs).then((priceSummary) => {
                            resolve(priceSummary);
                        });
                    });
                }
                return $q.resolve(new SubscriptionChangePriceSummary());
            }

            /**
             * Checks if a site's plan has been changed.
             *
             * @param site The site to check whether it's been changed or not.
             */
            function planChangeCheck(site) {
                const originalSite = _.find(originalSites, { id: site.id });

                site.changed = site.plan.identifier !== originalSite.plan.identifier;

                // Remove any details we already stored from the changes.plans model object.
                // We will re-add it with the new plan details if needed.
                _.remove($scope.changes.plans, { id: site.id });

                // If details changed, store this in the changes.plans model object.
                if (site.changed) {
                    $scope.changes.plans.push({
                        id: site.id,
                        plan: site.plan,
                        always_on_rate: site.always_on_rate,
                        percentage_discount: site.percentage_discount,
                        original: {
                            plan: originalSite.plan,
                            always_on_rate: originalSite.always_on_rate
                        }
                    });
                }
            }

            /**
             * Determine what type of subscription note we need to show at the top of the page.
             */
            function subscriptionNoteType() {
                var billingCycle = account.billing_cycle,
                    becameACustomer = UserData.get('becameACustomer'),
                    daysSinceBecomingACustomer = $filter('daysAgo')(becameACustomer),
                    noteType = null;

                if (billingCycle === 'yearly') {
                    noteType = 'manualPayment';
                } else if (becameACustomer === null || daysSinceBecomingACustomer < 30) {
                    noteType = 'moneyBack';
                } else if (daysSinceBecomingACustomer > 30 && billingCycle === 'monthly') {
                    noteType = 'annualUpsell';
                }

                return noteType;
            }

            /**
             * Reset submitting state.
             */
            function resetSubmit() {
                $scope.model.submitting = false;
            }

            /**
             * Call the Subscription update service.
             *
             * @param account Account we are doing changes for.
             * @param paymentData Payment data to pass to Subscription service.
             */
            function callSubscriptionUpdate(account, paymentData, showPlanChangeConfirmation) {
                return Subscription.update(
                    account,
                    paymentData,
                    undefined,
                    paymentData.defaultBillingCycle,
                    showPlanChangeConfirmation
                )
                    .then(() => {
                        if (!$scope.model.zuoraEnabled) {
                            Notify.success('Subscription changed successfully', {
                                when: 'onReload'
                            });
                            $window.location.reload();
                        }
                        checkTransactionStatus();
                    })
                    .catch(function (error) {
                        if (error?.message !== 'Cancelled') {
                            Notify.unhandledError();
                            // Silence this re-throw until appcues issue is resolved
                            // throw new Error('Subscription update failed');
                        }

                        resetSubmit();
                    });
            }

            function getCostSpecs(sites) {
                return sites.map((site) => new SitePlanInfo(site.id, site.plan_id));
            }

            /*************************
             * SCOPE FUNCTIONS
             *************************/

            /**
             * Restores the sample_rate and/or always_on_rate to its original value to cancel a downgrade.
             *
             * @param site Site object to be updated
             */
            $scope.cancelDowngrade = function (site) {
                if (site.is_in_plan_change_period) {
                    const costSpecs = [new SitePlanInfo(site.id, site.interim_plan_id)];

                    // Call subscription, not chaining on the result because we
                    // refresh the page to get the new state anyway.
                    callSubscriptionUpdate(
                        $scope.model.account,
                        new PaymentData(costSpecs, undefined, undefined, $scope.model.account.billing_cycle)
                    );
                }
            };

            /**
             * Update this account's subscription with the new plans.
             */
            $scope.updateSubscription = function updateSubscription() {
                $scope.model.submitting = true;

                const costSpecs = getCostSpecs(sites);

                const paymentData = new PaymentData(
                    costSpecs,
                    $scope.subscriptionInfo.paymentMethod,
                    MODAL_LOADED_REFERRER.updatePlans
                );
                const doUpdateSubscription = _.partial(callSubscriptionUpdate, $scope.model.account, paymentData);

                return Track.withContext({ origin: Track.paymentOrigins.modalPlanChangeConfirmation }, () => {
                    return doUpdateSubscription(true).then(() => {
                        $q.resolve(resetSubmit);
                    });
                });
            };

            /**
             * Cancel any plan changes the user has made & initialize.
             */
            $scope.cancelPlanChanges = function () {
                // TODO(rob.lester): This is quick and very dirty, just
                // re-initialize the whole model.
                //
                // We don't need to do all this, but we'd need to break up the model
                // from 'loaded once' data, and data that can be refreshed.
                //
                // I.e we can undo all the changes, but then we have to fetch and
                // update the price info again and ensure that `changes` are
                // refreshed too.
                return init();
            };

            /**
             * Update Payment Method.
             * @param {string} [referrer] From where was this action called. Defaults to "Plans and Billing"
             * @param {string} [defaultBillingCycle] Billing cycle to pre-select when entering payment.
             * @param {boolean} [editPaymentMethod] Set false to show existing payment details when the modal loads.
             */
            $scope.updatePaymentMethod = function (
                referrer = 'Plans and Billing',
                defaultBillingCycle,
                editPaymentMethod
            ) {
                Track.click('Edit Payment Method', 'Plans and Billing Page', {});
                if (defaultBillingCycle === 'yearly_recurring') {
                    $scope.showYearlyUpsellModal(account, referrer);
                } else {
                    // experiment is only applied when `yearly_recurring` should be checked by default on original
                    // update payment modal. don't run experiment if that's not the case
                    const paymentData = new PaymentData(
                        undefined,
                        $scope.subscriptionInfo?.paymentMethod,
                        referrer,
                        defaultBillingCycle ?? $scope.model.account.billing_cycle
                    );
                    Subscription.updatePaymentMethod($scope.model.account, paymentData, editPaymentMethod).then(() => {
                        $window.location.reload();
                    });
                }
            };

            /**
             * Delete the account's payment method after showing a confirmation modal.
             */
            $scope.deletePaymentMethod = function () {
                $rootScope.$emit('paymentMethodChanged');
            };

            /**
             * Contact Hotjar support.
             */

            // Function to partially apply an event source before opening a new
            // billing and payment ticket form.
            function newBillingPaymentTicket(source) {
                return () => {
                    Help.openChat('newBillingPaymentTicket', source);
                };
            }

            // Create multiple ways of opening billing and payment tickets by
            // partially applying specific event sources.
            $scope.contactSupport = newBillingPaymentTicket('Plans & Billing Contact');
            $scope.contactSupportManualPayments = newBillingPaymentTicket('Change Billing and Payment');

            /**
             * Change subscription from monthly to yearly and vice versa, or
             * show upsell modal when switching from basic
             */
            $scope.onChangeSubscription = function (switchTo) {
                switch (switchTo) {
                    case 'monthly':
                        $window.open(SUPPORT_URLS.createBillingPaymentTicket, '_blank');
                        return;
                    case 'business':
                        Subscription.upsell(SUBSCRIPTION_COST_SUMMARY_BUSINESS);
                        return;
                    case 'yearly_recurring':
                        // show update payment method modal
                        $scope.updatePaymentMethod('Plans and Billing', switchTo, false);
                        return;
                    default:
                        return;
                }
            };

            /**
             * Change a site's plan and sample rate.
             *
             * @param {organizationId} number - ID of the organization of the changed site
             * @param {siteId} number - ID of the changed site
             * @param {Object} Plan - Selected new plan
             */
            $scope.changeSitePlanId = function (organizationId, siteId, plan) {
                const site = sites.find((site) => site.id === siteId);
                // NOTE(rob.lester):
                // We're mutating the site here, which is generally poor-form, but
                // what's updating our renderable model effectively, so
                // the plan dropdowns update.
                site.plan_id = plan.identifier;
                site.plan = plan;

                const costSpecs = getCostSpecs(sites);

                const currencySymbol = window.settings.currency_symbols[account.currency];
                return initPriceInfo(costSpecs).then((subscriptionChangePriceSummary) => {
                    const { future: priceSummary } = subscriptionChangePriceSummary;
                    $scope.updatedSitePriceInfo = sites.reduce((acc, site) => {
                        return {
                            ...acc,
                            [site.id]: SitePriceInfo.fromLineItem(
                                priceSummary.lineItems.find(
                                    (item) => item.sitePlanInfo.siteReference.siteId === site.id
                                ),
                                currencySymbol
                            )
                        };
                    }, {});

                    // NOTE(rob.lester):
                    // planChangeCheck mutates the scope which will cause a digest,
                    // so we have to do it here to group the mutations in 1 place.
                    planChangeCheck(site);

                    // Since we've updated our plans, we should keep track of the
                    // 'active' subscription cost, i.e the cost of the selected
                    // plans.
                    $scope.subscriptionPriceSummary = subscriptionChangePriceSummary;

                    // Update the cost summary of the update summary bar based on
                    // our new price summary.
                    $scope.changes.cost = CostSummary.fromPriceSummary(priceSummary, currencySymbol);
                });
            };

            $scope.upsellToYearly = function () {
                $scope.updatePaymentMethod(
                    'Plans and Billing: Save 20% with yearly billing button',
                    'yearly_recurring',
                    false
                );
            };
        }
    );
